import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Grid, Typography, styled } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CloudQueueOutlinedIcon from "@mui/icons-material/CloudQueueOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";

const Label = styled(Typography)({
  fontSize: "0.78rem",
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#2f81ad",
  marginBottom: 14,
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2.4rem",
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  color: "#0f172a",
  marginBottom: theme.spacing(2),
  maxWidth: 760,
  [theme.breakpoints.down("md")]: { fontSize: "1.95rem" },
  [theme.breakpoints.down("sm")]: { fontSize: "1.65rem" },
}));

const Lead = styled(Typography)({
  fontSize: "1.02rem",
  lineHeight: 1.65,
  color: "rgba(15, 23, 42, 0.65)",
  maxWidth: 720,
});

const Card = styled(Box)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3.5, 3.25),
  borderRadius: 10,
  backgroundColor: "#fff",
  border: "1px solid rgba(15, 23, 42, 0.1)",
  display: "flex",
  flexDirection: "column",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    borderColor: "rgba(47, 129, 173, 0.5)",
    boxShadow: "0 8px 24px -12px rgba(15, 23, 42, 0.15)",
    "& .industry-link": { gap: 10, color: "#1d648c" },
  },
}));

const IconBadge = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(47, 129, 173, 0.08)",
  color: "#2f81ad",
  marginBottom: 20,
  "& svg": { fontSize: 22 },
});

const CardHeading = styled(Typography)({
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 8,
  letterSpacing: "-0.01em",
});

const CardCopy = styled(Typography)({
  fontSize: "0.92rem",
  lineHeight: 1.6,
  color: "rgba(15, 23, 42, 0.65)",
  marginBottom: 18,
  flex: 1,
});

const ReadMore = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#2f81ad",
  transition: "gap 0.2s ease, color 0.2s ease",
  "& svg": { fontSize: 16 },
});

interface Industry {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const INDUSTRIES: Industry[] = [
  {
    icon: <AccountBalanceOutlinedIcon />,
    title: "Fintech",
    description:
      "Trading platforms, payments, broker integrations, and KYC workflows with sub-second latency and SOC 2-ready architecture.",
  },
  {
    icon: <LocalHospitalOutlinedIcon />,
    title: "Healthcare",
    description:
      "HIPAA-compliant clinical workflows, patient portals, and ML-powered diagnostic tools with strict data governance.",
  },
  {
    icon: <CloudQueueOutlinedIcon />,
    title: "SaaS",
    description:
      "Multi-tenant platforms, billing, role-based access control, and analytics dashboards for B2B SaaS products at scale.",
  },
  {
    icon: <LocalShippingOutlinedIcon />,
    title: "Logistics",
    description:
      "Real-time tracking, dispatch dashboards, and carrier-integration layers for transport and supply chain operators.",
  },
  {
    icon: <SchoolOutlinedIcon />,
    title: "Education",
    description:
      "Learning management systems, course delivery, certification, and mobile training apps for institutions and online educators.",
  },
  {
    icon: <ShoppingBagOutlinedIcon />,
    title: "E-commerce",
    description:
      "Headless storefronts, custom checkout flows, subscription billing, and inventory systems for modern commerce brands.",
  },
];

const IndustriesSection: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setVisible(true),
      { threshold: 0.1 }
    );
    obs.observe(node);
    return () => obs.unobserve(node);
  }, []);

  return (
    <Box
      ref={ref}
      component="section"
      aria-labelledby="industries-heading"
      sx={{
        py: { xs: 8, md: 11 },
        backgroundColor: "#fff",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            mb: { xs: 5, md: 7 },
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.5s ease, transform 0.5s ease",
          }}
        >
          <Label>Industries</Label>
          <SectionTitle id="industries-heading" variant="h2">
            Industry-specific experience
          </SectionTitle>
          <Lead>
            We understand how different industries work and where things go
            wrong. That insight helps us identify real problems, build
            user-centric tools, and deliver software that meets the day-to-day
            needs of your domain.
          </Lead>
        </Box>

        <Grid container spacing={2.5}>
          {INDUSTRIES.map((industry, i) => (
            <Grid item xs={12} sm={6} md={4} key={industry.title}>
              <Box
                sx={{
                  height: "100%",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(12px)",
                  transition: `opacity 0.45s ease ${
                    0.05 + i * 0.05
                  }s, transform 0.45s ease ${0.05 + i * 0.05}s`,
                }}
              >
                <Card>
                  <IconBadge>{industry.icon}</IconBadge>
                  <CardHeading variant="h3">{industry.title}</CardHeading>
                  <CardCopy>{industry.description}</CardCopy>
                  <ReadMore className="industry-link">
                    Read more <ArrowForwardIcon />
                  </ReadMore>
                </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default IndustriesSection;
